import NoResults from "@components/atoms/NoResults";
import { Prisma } from "@prisma/client";
import { getMonths } from "@shared/constants";
import {
  getEventTimeDateInfo,
  getEventTimezoneOffset,
  getHoursAndMinutesFromISODate,
  getNewDate,
  getTimezonesData,
} from "@shared/utils";
import { useTranslation } from "react-i18next";

import EventListComponent from "./EventListComponent";
import styles from "./style.module.scss";

export default function ListEventsByMonthYear({
  year,
  setIsLoading,
  events,
  moderation,
  timezonesData,
  usersTimezoneData,
  usersTimezoneLocation,
  yearOfEvents,
  listEvents,
  isSiteAdminPage,
  rangeDate,
  screenSize,
  currentSubTabCalendar,
  editEvents,
  setIsOpen,
  setSelectedEvent,
}: {
  year: string;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  events: Prisma.EventGetPayload<{
    include: {
      Partner: true;
      LocatorCompany: true;
      EventTypes: true;
      region: true;
    };
  }>[];
  moderation: boolean;
  listEvents: boolean;
  isSiteAdminPage: boolean;
  timezonesData: ReturnType<typeof getTimezonesData>;
  usersTimezoneData: { currentTimeOffsetInMinutes: number; label: string };
  usersTimezoneLocation: string;
  yearOfEvents: number[];
  rangeDate: {
    startDate: string | null;
    endDate: string | null;
  };
  screenSize: {
    width: number;
    height: number;
  };
  currentSubTabCalendar: string;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectedEvent: React.Dispatch<
    React.SetStateAction<Record<string, unknown>>
  >;
  editEvents: boolean;
}) {
  const { t } = useTranslation();

  const getIsEventInRange = (event, rangeDate) => {
    const eventTimezoneOffset = getEventTimezoneOffset({
      eventTimezone: event.timezone,
      timezonesData,
    });
    const { startDate } = getEventTimeDateInfo({
      event,
      timezoneHoursOffset:
        usersTimezoneData.currentTimeOffsetInMinutes / 60 -
        eventTimezoneOffset / 60,
    });

    return startDate >= rangeDate.startDate && startDate <= rangeDate.endDate;
  };

  const getEventTimeInfo = (event) => {
    const eventTimezoneOffset = getEventTimezoneOffset({
      eventTimezone: event.timezone,
      timezonesData,
    });

    const { startDate, endDate } = getEventTimeDateInfo({
      event,
      timezoneHoursOffset:
        usersTimezoneData.currentTimeOffsetInMinutes / 60 -
        eventTimezoneOffset / 60,
    });

    const eventYear = startDate.split("T")[0].split("-")[0];
    const eventMonth = startDate.split("T")[0].split("-")[1];

    const differentDays = startDate.split("T")[0] !== endDate.split("T")[0];

    const eventTimeLabel = event.allDayEvent
      ? t("all_day")
      : getHoursAndMinutesFromISODate(startDate);

    return {
      eventYear,
      eventMonth,
      startDate,
      endDate,
      differentDays,
      eventTimeLabel,
    };
  };
  const areThereEvents = events.some((event) => {
    if (moderation) {
      return (
        event.contentStatus !== "ACCEPTED" &&
        getIsEventInRange(event, {
          startDate: `${
            yearOfEvents ? yearOfEvents[yearOfEvents?.length - 1] : ""
          }-01-01T00:00:00.000Z`,
          endDate: `${yearOfEvents ? yearOfEvents[0] : ""}-12-31T00:00:00.000Z`,
        })
      );
    }

    return (
      listEvents &&
      (isSiteAdminPage ||
        (!isSiteAdminPage && event.contentStatus === "ACCEPTED")) &&
      getIsEventInRange(event, rangeDate)
    );
  });

  const months = getMonths(t);

  let monthHeader: string;
  let yearHeader: string;

  return (
    <div key={`year_${year}`}>
      {areThereEvents ? (
        months.map((month, index) => {
          const [yearStart, yearEnd] = [year, year];
          const monthEvent = events
            .filter((event) => {
              if (moderation)
                return (
                  event.contentStatus !== "ACCEPTED" &&
                  getIsEventInRange(event, {
                    startDate: `${year}-01-01T00:00:00.000Z`,
                    endDate: `${year}-12-31T00:00:00.000Z`,
                  })
                );

              return (
                listEvents &&
                (isSiteAdminPage ||
                  (!isSiteAdminPage && event.contentStatus === "ACCEPTED")) &&
                getIsEventInRange(event, rangeDate)
              );
            })
            .filter((event) =>
              getIsEventInRange(event, {
                startDate:
                  getNewDate({
                    date: `${year}-01-01T00:00:00.000Z`,
                    usersTimezoneLocation,
                    getISODateFromUsersTimezone: true,
                  }).split("T")[0] + "T00:00:00.000Z",
                endDate:
                  getNewDate({
                    date: `${year}-12-31T00:00:00.000Z`,
                    usersTimezoneLocation,
                    getISODateFromUsersTimezone: true,
                  }).split("T")[0] + "T23:59:59.999Z",
              })
            )
            .filter((event) => {
              const { eventMonth, eventYear } = getEventTimeInfo(event);

              return (
                Number(eventMonth) === Number(month.number) &&
                Number(eventYear) >= Number(yearStart) &&
                Number(eventYear) <= Number(yearEnd)
              );
            });

          if (monthEvent?.length > 0) {
            return (
              <div key={`event_${month.name}_${index}`}>
                {monthEvent.map((event) => {
                  const {
                    eventYear,
                    startDate,
                    endDate,
                    differentDays,
                    eventTimeLabel,
                  } = getEventTimeInfo(event);

                  const showMonthHeader =
                    monthHeader !== month.name || yearHeader !== year;

                  monthHeader = month.name;
                  yearHeader = year;

                  return listEvents && screenSize.width <= 1208 ? (
                    <div className={styles.event_box} key={`event_${event.id}`}>
                      <EventListComponent
                        currentSubTabCalendar={currentSubTabCalendar}
                        differentDays={differentDays}
                        editEvents={editEvents}
                        endDate={endDate}
                        event={event}
                        eventTimeLabel={eventTimeLabel}
                        eventYear={year}
                        isSiteAdminPage={isSiteAdminPage}
                        key={`event_comp_1_${event.id}`}
                        moderation={moderation}
                        month={month}
                        screenSize={screenSize}
                        setIsOpen={setIsOpen}
                        setSelectedEvent={setSelectedEvent}
                        showMonthHeader={showMonthHeader}
                        startDate={startDate}
                        timezonesData={timezonesData}
                        usersTimezoneData={usersTimezoneData}
                      />
                      <div className={styles.event_lower}>
                        {screenSize.width < 590 ? (
                          <div className={styles.event_title}>
                            <span>
                              {event.title.length > 50
                                ? `${event.title.slice(0, 50)}...`
                                : event.title}
                            </span>
                            <span className={styles.tagline}>
                              {event.tagline.length > 50
                                ? `${event.tagline.slice(0, 50)}...`
                                : event.tagline}
                            </span>
                          </div>
                        ) : (
                          <div className={styles.event_title}>
                            <span>
                              {event.title.length > 80
                                ? `${event.title.slice(0, 80)}...`
                                : event.title}
                            </span>
                            <span className={styles.tagline}>
                              {event.tagline.length > 80
                                ? `${event.tagline.slice(0, 80)}...`
                                : event.tagline}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <EventListComponent
                      currentSubTabCalendar={currentSubTabCalendar}
                      differentDays={differentDays}
                      editEvents={editEvents}
                      endDate={endDate}
                      event={event}
                      eventTimeLabel={eventTimeLabel}
                      eventYear={eventYear}
                      isSiteAdminPage={isSiteAdminPage}
                      key={`event_comp_2_${event.id}`}
                      moderation={moderation}
                      month={month}
                      screenSize={screenSize}
                      setIsOpen={setIsOpen}
                      setSelectedEvent={setSelectedEvent}
                      showMonthHeader={showMonthHeader}
                      startDate={startDate}
                      timezonesData={timezonesData}
                      usersTimezoneData={usersTimezoneData}
                    />
                  );
                })}
              </div>
            );
          }
        })
      ) : (
        <NoResults sadImage text={t("no_events_list")} />
      )}
    </div>
  );
}
